import React from 'react';
import PropTypes from 'prop-types';
import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

const propTypes = {
  id: PropTypes.string,
  availableLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

const FrVersion = ({ id, availableLanguages }) => {
  return (
    <>
      <h4 id={id || 'déclaration-de-confidentialité'}>
        Tous les publics <br />
        Déclaration de confidentialité
      </h4>

      <p>Langues disponibless:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Date d’entrée en vigueur: 22 février 2024</p>
      <p>
        Merci d’utiliser Plaid ! Nous espérons que vous prendrez le temps de
        lire attentivement la présente Déclaration de confidentialité.
      </p>
      <p>
        La présente Déclaration de confidentialité explique comment Plaid Inc.
        et ses filiales, y compris Plaid Financial Ltd., Plaid Consumer
        Reporting Agency, Inc. et Plaid, B.V., (collectivement, « Plaid », «
        nous », « notre » et « nos ») recueillent, utilisent et partagent des
        informations vous concernant (c.-à-d. le développeur, le client, le
        visiteur du site Web) lorsque vous consultez ou utilisez les sites Web
        de Plaid, intégrez nos produits et services dans vos applications
        logicielles ou interagissez autrement avec nous (par exemple, lorsque
        vous demandez des renseignements auprès du service client ou que vous
        participez à un événement ou à un webinar de Plaid). La présente
        Déclaration de confidentialité ne s’applique pas aux informations que
        nous recueillons sur les utilisateurs finaux lorsqu’ils connectent leurs
        comptes financiers à une application via Plaid ni aux données utilisées
        pour générer un rapport de solvabilité de Plaid CRA. Si vous recherchez
        des informations sur la manière dont nous traitons les données
        recueillies dans ces circonstances, veuillez consulter notre{' '}
        <Link href='#politique-de-confidentialité-destinée-aux-utilisateurs-finaux'>
          <a>Politique de confidentialité de l’utilisateur final</a>
        </Link>{' '}
        ou la{' '}
        <Link href='/plaid-check-consumer-report/privacy-policy/'>
          <a>Politique de confidentialité de Plaid Consumer Reporting Agency</a>
        </Link>
        .
      </p>
      <p>
        <em>Aller à la section:</em>
      </p>
      <p>
        <Link href='#nos-pratiques-en-matière-de-données'>
          <a data-smooth-scroll data-offset='80'>
            Nos pratiques en matière de données
          </a>
        </Link>
      </p>

      <ul data-smooth-scroll data-offset='80'>
        <li>
          <p>
            <Link href='#informations-que-nous-recueillons-et-catégories-de-sources'>
              <a>Informations que nous recueillons et catégories de sources</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#l’utilisation-que-nous-faisons-de-vos-données'>
              <a>L’utilisation que nous faisons de vos données</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#nos-bases-légales-pour-le-traitement-dans-l’EEE-et-au-royaume-uni'>
              <a>
                Nos bases légales pour le traitement dans l’EEE et au
                Royaume-Uni
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#le-partage-de-vos-données'>
              <a>Le partage de vos données</a>
            </Link>
          </p>
        </li>
      </ul>

      <p>
        <Link href='#autres-informations'>
          <a data-smooth-scroll data-offset='80'>
            Autres informations
          </a>
        </Link>
      </p>

      <ul data-smooth-scroll data-offset='80'>
        <li>
          <p>
            <Link href='#chatbots-publicité-analyse-et-autres-services-fournis-par-des-tiers'>
              <a>
                Chatbots, publicité, analyse et autres services fournis par des
                tiers
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#transferts-internationaux-de-données'>
              <a>Transferts internationaux de données</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#conservation-des-données'>
              <a>Conservation des données</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#vos-choix-données-personnelles'>
              <a>Vos choix</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#vos-droits-en-matière-de-protection-des-données'>
              <a>Vos droits en matière de protection des données</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#modifications-apportées-à-cette-déclaration'>
              <a>Modifications apportées à cette déclaration</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#contacter-plaid-données-personnelles'>
              <a>Contacter Plaid</a>
            </Link>
          </p>
        </li>
      </ul>
      <br />
      <br />

      <h4 id='nos-pratiques-en-matière-de-données'>
        Nos pratiques en matière de données
      </h4>
      <h5 id='informations-que-nous-recueillons-et-catégories-de-sources'>
        Informations que nous recueillons et catégories de sources
      </h5>
      <p>
        <strong>Informations que vous nous confiez</strong>. Nous recueillons
        les informations que vous fournissez directement à Plaid. Par exemple,
        vous fournissez des informations personnelles lorsque vous créez un
        compte de développeur, remplissez notre formulaire « Nous contacter »,
        souscrivez à nos e-mails, demandez de l’assistance auprès du service
        client, créez un compte de facturation ou communiquez autrement avec
        nous. Les types d’informations que nous recueillons comprennent :
      </p>
      <ul>
        <li>
          <p>
            des identifiants (comme le nom complet, l’adresse e-mail, l’adresse
            postale, le numéro de téléphone et la date de naissance),
          </p>
        </li>
        <li>
          <p>
            des informations sensibles (comme le numéro de permis de conduire et
            le numéro d’identification fiscale, les informations de compte
            financier et les numéros de sécurité sociale nécessaires à la
            prestation des services),
          </p>
        </li>
        <li>
          <p>
            des informations professionnelles (comme le nom de la société et
            l’intitulé de poste), et
          </p>
        </li>
        <li>
          <p>
            toute autre information personnelle que vous choisissez de fournir
            (y compris, par exemple, dans les messages d’assistance aux
            développeurs).
          </p>
        </li>
      </ul>
      <p>
        Nous pouvons également recueillir certains documents où apparaît votre
        photographie ou vous demander de vérifier votre identité en nous
        fournissant une photographie ou une vidéo de vous-même. Les données
        recueillies dans ce contexte peuvent être considérées comme des données
        biométriques dans certains États ou pays, comme indiqué ci-dessous. Voir
        l’avis spécial pour les résidents de l’Illinois et du Texas ci-dessous.
      </p>
      <p>
        <u>
          Avis spécial relatif aux données biométriques pour les résidents de
          l’Illinois et du Texas
        </u>
      </p>
      <p>
        Pour les résidents de l’Illinois ou du Texas, si nous exigeons que vous
        nous fournissiez un document contenant votre photographie ou si vous
        avez besoin de prouver votre identité en fournissant une photographie ou
        une vidéo de vous-même, les données dérivées de votre visage que nous et
        nos prestataires de services recueillons et traitons en notre nom
        peuvent être considérées comme des données biométriques dans certaines
        juridictions. Vos données ne seront pas conservées plus de trois ans,
        sauf si la loi l’exige.
      </p>
      <p>
        <strong>
          Informations que nous recevons lorsque vous testez notre technologie
        </strong>
        . Parfois, les personnes qui créent des applications à l’aide de nos
        services utilisent leurs propres informations de connexion à leur compte
        bancaire ou autre compte financier afin de tester et d’évaluer
        l’apparence et le fonctionnement de notre technologie dans leur
        application. Si vous le faites, vous devez lire notre{' '}
        <Link href='#politique-de-confidentialité-destinée-aux-utilisateurs-finaux'>
          <a>Politique de confidentialité de l’utilisateur final</a>
        </Link>{' '}
        pour en savoir plus sur la manière dont nous recueillons et traitons ces
        informations.
      </p>
      <p>
        <strong>
          Informations que nous recueillons lorsque vous utilisez nos services
        </strong>
        . Lorsque vous utilisez nos services, nous recueillons les informations
        suivantes vous concernant :
      </p>
      <ul>
        <li>
          <p>
            <strong>Informations du journal</strong>: Nous recueillons des
            fichiers journaux lorsque vous utilisez nos services. Cela inclut
            les identifiants et les informations d’activité du réseau
            électronique, comme le type de navigateur que vous utilisez lorsque
            vous accédez à nos services, les pages consultées et votre adresse
            IP.
          </p>
        </li>
        <li>
          <p>
            <strong>Localisation approximative</strong>: Nous déterminons la
            localisation approximative de l’appareil que vous utilisez pour
            accéder à nos services à partir de votre adresse IP ou du fuseau
            horaire utilisé sur votre appareil.
          </p>
        </li>
        <li>
          <p>
            <strong>
              Informations recueillies par les cookies et autres technologies de
              suivi
            </strong>
            : Nous utilisons diverses technologies, comme les cookies et les
            balises Web dans nos services, pour recueillir des informations
            personnelles. Les cookies sont de petits fichiers de données stockés
            par votre navigateur Web, sur votre disque dur ou dans la mémoire de
            votre appareil, qui nous aident à améliorer nos services et votre
            expérience, à déterminer comment vous utilisez nos services, et à
            surveiller et détecter toute conduite potentiellement nuisible. Les
            balises Web sont des images électroniques qui sont utilisées dans
            nos services ou dans nos e-mails et aident à fournir des cookies.
            Les balises Web comptent le nombre de fois qu’une page Web est
            consultée et nous aident à comprendre comment notre Service est
            utilisé et de quelle manière l’améliorer.
          </p>
          <ul>
            <li>
              <p>
                Veuillez noter que nous pouvons utiliser des cookies tiers à des
                fins publicitaires, comme le permet la loi et comme décrit plus
                en détail dans notre{' '}
                <Link href='#politique-relative-aux-cookies'>
                  <a>Politique relative aux cookies</a>
                </Link>
                . Pour modifier vos paramètres et préférences en matière de
                cookies, y compris ceux concernant les cookies de ciblage et
                publicitaires, cliquez sur le lien Préférences en matière de
                cookies en bas de page.
              </p>
            </li>
          </ul>
        </li>
      </ul>
      <p>
        <strong>
          Informations que nous recueillons auprès d’autres sources
        </strong>
        . Nous recueillons également des informations vous concernant auprès
        d’autres sources. Par exemple, nous pouvons recueillir des informations
        auprès d’autres membres de votre société et de fournisseurs qui nous
        aident à identifier de nouveaux clients commerciaux, notamment votre
        nom, votre adresse e-mail et l’URL de votre profil sur les réseaux
        sociaux.
      </p>

      <h5 id='l’utilisation-que-nous-faisons-de-vos-données'>
        L’utilisation que nous faisons de vos données
      </h5>
      <p>
        Nous utilisons les informations que nous recueillons aux fins
        commerciales suivantes:
      </p>
      <ul>
        <li>
          <p>Pour exploiter, améliorer et développer nos services;</p>
        </li>
        <li>
          <p>Pour vérifier votre identité;</p>
        </li>
        <li>
          <p>Pour facturer nos services aux développeurs et pour les payer;</p>
        </li>
        <li>
          <p>
            Pour nous conformer à la loi, par exemple à des fins de déclaration
            fiscale;
          </p>
        </li>
        <li>
          <p>
            Pour vous envoyer des informations telles que des avis techniques,
            des mises à jour, des alertes de sécurité et des messages concernant
            votre compte;
          </p>
        </li>
        <li>
          <p>
            Pour répondre à vos commentaires, vos questions et vos demandes
            auprès du service client;
          </p>
        </li>
        <li>
          <p>
            Pour vous aider à personnaliser votre expérience avec nos services;
          </p>
        </li>
        <li>
          <p>
            Pour communiquer avec vous au sujet des produits, services, offres
            et événements de Plaid que nous proposons ou parrainons;
          </p>
        </li>
        <li>
          <p>
            Pour surveiller et analyser les tendances, l’utilisation et les
            activités liées à nos services;
          </p>
        </li>
        <li>
          <p>
            Pour détecter et prévenir la fraude, les activités malveillantes et
            autres activités illégales;
          </p>
        </li>
        <li>
          <p>
            Pour protéger les droits, la vie privée, la sécurité ou la propriété
            de Plaid et d’autres personnes; et
          </p>
        </li>
        <li>
          <p>
            À d’autres fins dont nous vous informons avant ou au moment où nous
            recueillons les informations.
          </p>
        </li>
      </ul>

      <h5 id='nos-bases-légales-pour-le-traitement-dans-l’EEE-et-au-royaume-uni'>
        Nos bases légales pour le traitement dans l’EEE et au Royaume-Uni
      </h5>
      <p>
        Chez Plaid, nous traitons vos informations personnelles que lorsque nous
        avons une base juridique valide pour le faire. Notre base juridique pour
        le traitement des informations que nous recueillons dépendra des
        informations que nous avons recueillies et du contexte pour leur
        traitement. En règle générale, nous ne recueillons et ne traitons des
        informations que:
      </p>
      <ul>
        <li>
          <p>
            lorsque nous devons remplir nos responsabilités et obligations dans
            tout contrat ou accord avec vous;
          </p>
        </li>
        <li>
          <p>
            pour nous conformer à nos obligations légales en vertu de la loi
            applicable;
          </p>
        </li>
        <li>
          <p>
            lorsque le traitement est nécessaire pour nos intérêts légitimes et
            n’est pas outrepassé par vos intérêts en matière de protection des
            données ou vos droits et libertés fondamentaux (par exemple, pour
            fournir ou mettre à jour nos services); et
          </p>
        </li>
        <li>
          <p>lorsque vous avez donné votre consentement pour le faire.</p>
        </li>
      </ul>

      <h5 id='le-partage-de-vos-données'>Le partage de vos données</h5>
      <p>Nous partageons les informations vous concernant comme suit:</p>
      <ul>
        <li>
          <p>
            Avec d’autres sociétés ou personnes en tant que prestataires de
            services, sous-traitants ou partenaires commerciaux qui nous
            fournissent des services et nous aident à soutenir notre activité
            commerciale. Nous partageons également des informations avec des
            agences de recouvrement en cas de paiements en souffrance de la part
            de nos développeurs;
          </p>
        </li>
        <li>
          <p>
            Si nous croyons de bonne foi que la divulgation est appropriée pour
            nous conformer à la loi, à la réglementation ou à une procédure
            juridique applicable (telle qu’une ordonnance d’un tribunal ou une
            assignation). Cela inclut la divulgation en lien avec les demandes
            des forces de l’ordre ou d’autres autorités gouvernementales;
          </p>
        </li>
        <li>
          <p>
            Si nous pensons que vos actions sont incompatibles avec nos accords
            ou politiques, ou pour protéger les droits, la vie privée, la
            sécurité ou la propriété de Plaid ou d’autres personnes;
          </p>
        </li>
        <li>
          <p>
            En cas de changement de propriété ou de contrôle de tout ou partie
            de nos activités (comme une fusion, une acquisition, une
            réorganisation ou une faillite);
          </p>
        </li>
        <li>
          <p>
            Entre Plaid et nos sociétés mères, sociétés affiliées, filiales et
            autres sociétés sous une direction commune ou en copropriété,
            actuelles et futures; et
          </p>
        </li>
        <li>
          <p>Avec votre consentement ou à votre demande.</p>
        </li>
      </ul>
      <p>
        Nous recueillons, utilisons et partageons également les informations qui
        ont été agrégées et anonymisées de manière à ce qu’elles ne puissent pas
        être utilisées pour vous identifier, à toute fin autorisée par la loi
        applicable.
      </p>
      <p>
        Nous pouvons partager des Données à caractère personnel avec des tiers
        ou leur permettre de recueillir des Données à caractère personnel à
        partir de nos sites ou services, comme décrit ci-dessus.
      </p>
      <p>
        Ce tableau explique comment nous avons recueilli et partagé des
        informations personnelles au cours des 12 derniers mois.
      </p>
      <br />

      <p>CATÉGORIES D’INFORMATIONS PERSONNELLES</p>

      <div className='all-audiences-container'>
        <table className='all-audiences-table'>
          <thead>
            <tr>
              <td>
                <p>Catégorie d’informations personnelles</p>
              </td>
              <td>
                <p>Source des informations personnelles</p>
              </td>
              <td>
                <p>Utilisations des informations personnelles</p>
                <p>
                  (voir ci-dessus pour en savoir plus sur les cas d’utilisation
                  mentionnés ci-dessous)
                </p>
              </td>
              <td>
                <p>
                  Catégories de Parties avec lesquelles des informations
                  personnelles peuvent être partagées
                </p>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <ul>
                  <li>
                    <p>Identifiants</p>
                  </li>
                  <li>
                    <p>Informations financières</p>
                  </li>
                  <li>
                    <p>Informations commerciales</p>
                  </li>
                  <li>
                    <p>Informations sur la localisation</p>
                  </li>
                  <li>
                    <p>Informations profession-nelles</p>
                  </li>
                  <li>
                    <p>
                      Informations sur l’activité sur le réseau électronique
                    </p>
                  </li>
                  <li>
                    <p>Informations sensibles</p>
                  </li>
                  <li>
                    <p>Inférences</p>
                  </li>
                </ul>
              </td>
              <td>
                <ul>
                  <li>
                    <p>Données que vous nous confiez</p>
                  </li>
                  <li>
                    <p>Informations que nous recevons de vos appareils</p>
                  </li>
                  <li>
                    <p>
                      Informations que nous recevons à votre sujet d’autres
                      sources, comme détaillé ci-dessus.{' '}
                    </p>
                  </li>
                </ul>
              </td>
              <td>
                <ul>
                  <li>
                    <p>Fournir des services</p>
                  </li>
                  <li>
                    <p>Améliorer les services</p>
                  </li>
                  <li>
                    <p>
                      Prévenir la fraude et protéger la confidentialité et la
                      sécurité
                    </p>
                  </li>
                  <li>
                    <p>Fournir un soutien</p>
                  </li>
                  <li>
                    <p>
                      Enquêter sur les utilisations abusives et les violations
                    </p>
                  </li>
                  <li>
                    <p>Respecter les obligations légales</p>
                  </li>
                  <li>
                    <p>Avec votre consentement</p>
                  </li>
                </ul>
              </td>
              <td>
                <ul>
                  <li>
                    <p>
                      Prestataires effectuant des recherches et des enquêtes
                      auprès des utilisateurs
                    </p>
                  </li>
                  <li>
                    <p>
                      Fournisseurs d’outils de collaboration et de communication
                      internes
                    </p>
                  </li>
                  <li>
                    <p>Fournisseurs de services de messagerie</p>
                  </li>
                  <li>
                    <p>
                      Prestataires chargés de la gestion de la relation client
                      et fournissant de l’aide à la vente et au marketing
                    </p>
                  </li>
                  <li>
                    <p>
                      Fournisseurs chargés de la gestion et de l’hébergement
                      d’événements
                    </p>
                  </li>
                  <li>
                    <p>Prestataires de services d’assistance à la clientèle</p>
                  </li>
                  <li>
                    <p>Prestataires chargés de la génération de leads</p>
                  </li>
                  <li>
                    <p>Fournisseurs de services de gestion de documents</p>
                  </li>
                  <li>
                    <p>
                      Fournisseurs de stockage cloud et d’entreposage de données
                    </p>
                  </li>
                  <li>
                    <p>Fournisseurs de services de paiement</p>
                  </li>
                  <li>
                    <p>
                      Fournisseurs de services de prévention de la fraude, de
                      vérification de l’identité et de sécurité
                    </p>
                  </li>
                  <li>
                    <p>Fournisseurs de services de développement logiciel</p>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h4 id='autres-informations'>Autres informations</h4>
      <h5 id='chatbots-publicité-analyse-et-autres-services-fournis-par-des-tiers'>
        Chatbots, publicité, analyse et autres services fournis par des tiers
      </h5>
      <p>
        Nous autorisons des tiers, y compris Google Analytics, à fournir des
        services d’analyse et des publicités sur nos sites Web. Ces tiers
        utilisent des cookies, des balises Web et d’autres technologies pour
        recueillir des informations vous concernant. Plaid utilise des chatbots
        gérés par des prestataires de services tiers. Veuillez noter que si vous
        fournissez des informations personnelles à notre chatbot, ces
        informations pourront être partagées avec le prestataire de services
        tiers pour les besoins du chatbot.
      </p>
      <p>
        Vous trouverez plus d’informations sur la façon dont nous utilisons les
        cookies et sur vos choix connexes dans notre{' '}
        <Link href='#politique-relative-aux-cookies'>
          <a>Politique relative aux cookies</a>
        </Link>
        .
      </p>

      <h5 id='transferts-internationaux-de-données'>
        Transferts internationaux de données
      </h5>
      <p>
        Nous pouvons transférer les informations que nous recueillons à votre
        sujet vers d’autres pays que celui dans lequel vous vivez pour leur
        traitement et leur stockage. Si nous transférons des informations vers
        un pays ou un territoire qui n’a pas été considéré comme ayant des lois
        équivalentes à votre pays en matière de confidentialité et de protection
        des données, nous transférerons ces données conformément aux lois
        applicables en matière de protection des données. En particulier, nous
        pouvons utiliser des clauses contractuelles types approuvées par la
        Commission européenne ou le Secrétaire d’État britannique (le cas
        échéant), si nécessaire, en combinaison avec des garanties
        supplémentaires. Vous pouvez demander une copie de ces clauses
        contractuelles types en nous contactant comme indiqué ci-dessous.
      </p>
      <h5 id='conservation-des-données'>Conservation des données</h5>
      <p>
        Nous conservons vos données à caractère personnel pendant une durée ne
        dépassant pas celle nécessaire pour atteindre les objectifs pour
        lesquels elles ont été recueillies, à moins qu’une période de
        conservation plus longue ne soit requise en vertu de la loi applicable.
      </p>

      <h5 id='vos-choix-données-personnelles'>Vos choix</h5>
      <ul>
        <li>
          <p>
            <strong>Informations sur le compte du développeur</strong>. Vous
            pouvez mettre à jour les informations que vous nous fournissez pour
            votre compte de développeur en ligne en vous connectant à votre
            compte ou en nous contactant.
          </p>
        </li>
        <li>
          <p>
            <strong>Cookies</strong>. Vous trouverez plus d’informations sur la
            façon dont nous utilisons les cookies et sur vos choix connexes dans
            notre{' '}
            <Link href='#politique-relative-aux-cookies'>
              <a>Politique relative aux cookies</a>
            </Link>
            .
          </p>
        </li>
        <li>
          <p>
            <strong>Communications marketing</strong>. Vous pouvez choisir de ne
            plus recevoir d’e-mails marketing de Plaid en suivant les
            instructions contenues dans ces e-mails. Si vous demandez à ne plus
            en recevoir, nous pouvons toujours vous envoyer d’autres types
            d’e-mails, tels que des e-mails d’assistance, de service et autres
            concernant votre compte ou notre relation commerciale en cours.
          </p>
        </li>
      </ul>

      <h5 id='vos-droits-en-matière-de-protection-des-données'>
        Vos droits en matière de protection des données
      </h5>
      <p>
        Vous pouvez bénéficier de droits liés à vos informations personnelles, y
        compris le droit de savoir comment nous recueillons, utilisons ou
        partageons vos informations. En outre, vous pouvez avoir le droit de
        demander l’accès à vos informations personnelles, ainsi que de chercher
        à mettre à jour, supprimer ou corriger ces informations. Nous honorerons
        votre demande d’exercice de ces droits, quelle que soit votre
        localisation. Vous ne serez pas victime de discrimination pour avoir
        exercé ces droits.
      </p>
      <p>
        Dans certaines régions, y compris l’EEE et le Royaume-Uni, vous pouvez
        bénéficier de droits supplémentaires. Ces droits comprennent le droit de
        : vous opposer au traitement de vos données à caractère personnel ou de
        nous demander de le restreindre ; révoquer le consentement à tout moment
        lorsque notre traitement est basé sur votre consentement ; et déposer
        une plainte auprès de votre autorité de contrôle locale (si vous êtes
        dans l’EEE, veuillez consulter{' '}
        <Link href='https://edpb.europa.eu/about-edpb/board/members_fr'>
          <a>https://edpb.europa.eu/about-edpb/board/members_fr</a>
        </Link>
        , et si vous êtes au Royaume-Uni, veuillez consulter{' '}
        <Link href='https://ico.org.uk/'>
          <a>www.ico.org.uk</a>
        </Link>{' '}
        pour obtenir les coordonnées).
      </p>
      <p>
        Pour Plaid Financial Ltd. comme pour Plaid, B.V., nous avons désigné un
        délégué à la protection des données (DPD) qui est également responsable
        des questions de supervision en lien avec cette Politique. Vous pouvez
        le contacter par e-mail à l’adresse{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        .
      </p>
      <p>
        Si vous résidez en Californie, sous réserve des limitations et
        exceptions prévues par la loi, certains droits supplémentaires peuvent
        vous être accordés en vertu de la Loi californienne sur la protection
        des consommateurs (California Consumer Protection Act, « CCPA »), telle
        que modifiée par la loi California Privacy Rights Act. Vous pouvez
        également demander l’accès aux catégories d’informations personnelles et
        à certaines informations personnelles spécifiques recueillies à votre
        sujet au cours des douze derniers mois (y compris les informations
        personnelles divulguées à des fins professionnelles) ; savoir si
        certaines de vos informations personnelles ont été « vendues » ou «
        partagées » et à qui ; refuser toute « vente » ou tout « partage » de
        vos informations personnelles si une société est engagée dans la « vente
        » ou le « partage » de vos informations ; et demander à une société de
        corriger les informations personnelles inexactes. Veuillez noter que,
        bien que nous ne vendions pas vos informations personnelles, nous
        pouvons les partager avec des tiers à des fins publicitaires, à l’aide
        de « cookies », comme décrit dans notre politique relative aux cookies.
      </p>
      <p>
        Comme décrit ci-dessus dans « Vos choix », si vous êtes l’un de nos
        développeurs, vous pouvez facilement accéder aux informations de votre
        compte en ligne en vous connectant à votre compte ou en nous contactant.
        Pour exercer autrement vos droits en matière de protection des données,
        le cas échéant, veuillez nous contacter comme décrit dans la section «
        Contacter Plaid » ci-dessous. Vous devrez peut-être fournir des
        informations supplémentaires pour confirmer votre identité avant que
        nous puissions répondre à votre demande.
      </p>
      <p>
        Si nous recevons votre demande d’un agent autorisé, nous pouvons
        demander la preuve que vous avez fourni à l’agent une autorisation
        écrite valide pour soumettre des demandes en votre nom. Si vous êtes un
        agent autorisé cherchant à faire une demande, veuillez nous contacter
        comme décrit dans la section « Contacter Plaid » ci-dessous.
      </p>

      <h5 id='modifications-apportées-à-cette-déclaration'>
        Modifications apportées à cette déclaration
      </h5>
      <p>
        Nous pouvons mettre à jour ou modifier la présente Déclaration de
        confidentialité périodiquement. Si nous apportons des modifications,
        nous changerons la Date d’entrée en vigueur en haut de la présente
        Déclaration. Nous pouvons également vous informer de tout changement par
        d’autres moyens, par exemple en plaçant un avis sur notre page d’accueil
        à l’adresse{' '}
        <Link href='https://plaid.com'>
          <a>https://plaid.com</a>
        </Link>{' '}
        ou en vous envoyant un e-mail. Nous vous recommandons de consulter la
        Déclaration de confidentialité chaque fois que vous accédez aux services
        ou que vous interagissez autrement avec nous pour rester informé de nos
        pratiques en matière de données et des choix qui s’offrent à vous.
      </p>

      <h5 id='contacter-plaid-données-personnelles'>Contacter Plaid</h5>
      <p>
        Si vous avez des questions concernant la présente Déclaration de
        confidentialité ou nos pratiques de confidentialité en général, veuillez
        nous contacter à l’adresse{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>{' '}
        ou par courrier à l’adresse:
      </p>
      <p>
        <em>Si vous résidez en dehors de l’EEE ou du Royaume Uni:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid Inc. <br />
        A l’attention de: Service juridique <br />
        PO Box 7775 #35278 <br />
        San Francisco, Californie 94120-7775, États-Unis
      </div>
      <br />
      <p>
        Pour Plaid Financial Ltd. comme pour Plaid, B.V., nous avons désigné un
        délégué à la protection des données (DPD) qui est également responsable
        des questions de supervision en lien avec cette Politique. Vous pouvez
        le contacter par e-mail à l’adresse{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        .
      </p>
      <p>
        <br />
        <em>Si vous résidez au Royaume-Uni:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid Financial Ltd. <br />
        A l’attention de: Service juridique <br />
        New Penderel House, 4th Floor <br />
        283-288 High Holborn <br />
        Londres, Royaume-Uni, WC1V 7HP
      </div>
      <p>
        <br />
        <em>Si vous résidez dans l’EEE:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid, B.V. <br />
        A l’attention de: Service juridique <br />
        Muiderstraat 1 <br />
        1011PZ Amsterdam <br />
        Pays-Bas
      </div>

      <hr className='section-divider' />
    </>
  );
};

FrVersion.propTypes = propTypes;
export default FrVersion;
